import {
  FluidAction,
  FluidAlignment,
  FluidButtonType,
  FluidContainerElementType,
  FluidDataLayerComponentConfiguration,
  FluidEmptyStateConfig,
  FluidEmptyStateTranslationOptions,
  FluidIconConfig,
  FluidTheme,
  FluidThemeInterface,
  FluidThemeVariant,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
} from '@stencil/core';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import { FluidImageConfig } from '../fluid-image/components/fluid-image.components';
import {
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateIcon,
  EmptyStateImage,
  EmptyStateTitle,
} from './components/fluid-empty-state-container.components';

/**
 * @displayName Empty State Container
 * @contextData Use an empty state component to communicate to people that no content is available to display. Use them to engage people and guide them on what is best to do next by including a button or a link to the next best action.
 */
@Component({
  tag: 'fluid-empty-state-container',
  styleUrl: 'fluid-empty-state-container.scss',
  shadow: true,
})
export class FluidEmptyStateContainer
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidEmptyStateContainerElement;

  // ================================================================ //
  // -- States

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A collection of buttons to display in the state container to give the
   * user options for their next action.
   *
   * NOTE: Must include a primary action.
   */
  @Prop({ mutable: true }) actions: FluidAction[];

  /**
   * Configures the title text to use for the empty state notification.
   */
  @Prop({ mutable: true }) titleText: string;

  /**
   * Configures the body text to use for the empty state notification.
   */
  @Prop({ mutable: true }) bodyText: string;

  /**
   * Configures the icon to use for the empty state.
   */
  @Prop({ mutable: true }) iconConfig: FluidIconConfig;

  /**
   * Allows an image to be configured instead of an icon.
   */
  @Prop({ mutable: true }) imageConfig: FluidImageConfig;

  /**
   * Configures where the empty state container is being used. If in a component,
   * titles, text and actions are centered, but for an empty page, they are
   * left aligned.
   */
  @Prop({ mutable: true }) type: FluidContainerElementType =
    FluidContainerElementType.COMPONENT;

  /**
   * Determins the background color of the container
   * @default FluidThemeVariant.DEFAULT (white)
   */
  @Prop({ mutable: true }) themeVariant: FluidThemeVariant =
    FluidThemeVariant.ATMOSPHERIC;

  /**
   * Allows the component to be configured with a single object.
   */
  @Prop({ mutable: true }) config: FluidEmptyStateConfig;

  // ================================================================ //
  // -- Events

  /**
   * Emits the string action of whatever action was clicked if it is configured
   * with a string action rather than a function.
   */
  @Event() actionClicked: EventEmitter<string>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.selectiveMapConfigToLocal(this.host, this.config);
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
    this.validateActions();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Validates that at least one action is a primary action IF actions
   * are configured.
   */
  validateActions() {
    if (!this.actions || this.actions?.length === 0) return;
    if (!this.actions.find((a) => a.type === FluidButtonType.PRIMARY)) {
      Utils.consoleWarn(
        this.host,
        'You must include at least one primary action in an empty state container'
      );
    }
  }

  // ================================================================ //
  // -- Local Methods

  private handleActionClicked(action: FluidAction): void {
    Utils.isFunction(action.action)
      ? Utils.asFunction(action.action)(action.identifier, action.data)
      : this.emitActionClicked(action);
  }

  private emitActionClicked(action: FluidAction): void {
    this.actionClicked.emit(action.identifier);
    sendEvent(this.host, this.parentLayerId, 'actionClicked', {
      data: action.data,
    });
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const styleClass = {
      'fluid-empty-state-container': true,
      [this.theme]: true,
      [`theme-${this.theme}`]: true,
      'variant-default': this.themeVariant === FluidThemeVariant.DEFAULT,
      'variant-atmospheric':
        this.themeVariant === FluidThemeVariant.ATMOSPHERIC,
    };

    return (
      <fluid-container
        margin={10}
        class={styleClass}
        padding={'75px 50px 50px 50px'}
        textAlign={
          this.type === 'page' ? FluidAlignment.LEFT : FluidAlignment.CENTER
        }>
        <EmptyStateImage imageConfig={this.imageConfig} type={this.type} />
        <EmptyStateIcon
          iconConfig={this.iconConfig}
          type={this.type}
          hasImage={!!this.imageConfig}
        />
        <EmptyStateTitle
          titleText={this.titleText}
          type={this.type}
          iconConfig={this.iconConfig}
          hasImage={!!this.imageConfig}
          fluidTranslate={this.fluidTranslate}
          i18nStore={this._i18nStore}
        />
        <EmptyStateBody
          bodyText={this.bodyText}
          fluidTranslate={this.fluidTranslate}
          i18nStore={this._i18nStore}
        />
        <EmptyStateActions
          actions={this.actions}
          handleActionClicked={(action: FluidAction) =>
            this.handleActionClicked(action)
          }
        />
      </fluid-container>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  @Prop({ mutable: true }) fluidTranslate: FluidEmptyStateTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;

  // ================================================================ //
  // -- Analytics

  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(this.host)?.id;
  }
}
